import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Eye, Send, CheckCircle2 } from "lucide-react";
import { API } from "@/contexts/AuthContext";

const FOHCustomerPreview = () => {
  const { yestimateId } = useParams();
  const [preview, setPreview] = useState(null);
  const [yestimate, setYestimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [garageLogo, setGarageLogo] = useState(null);
  const [garageName, setGarageName] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch garage logo
    axios.get(`${API}/public/garage/logo`)
      .then(res => {
        if (res.data.has_logo && res.data.logo_data) {
          setGarageLogo(res.data.logo_data);
        }
        if (res.data.garage_name) {
          setGarageName(res.data.garage_name);
        }
      })
      .catch(() => {
        // Silently fail - logo is optional
      });
    
    Promise.all([
      axios.get(`${API}/foh/yestimate/${yestimateId}/preview`),
      axios.get(`${API}/foh/yestimate/${yestimateId}`)
    ]).then(([previewRes, yestimateRes]) => {
      setPreview(previewRes.data);
      setYestimate(yestimateRes.data.yestimate);
    }).catch(err => {
      alert(err.response?.data?.detail || "Failed to load preview");
      navigate("/app/foh");
    }).finally(() => setLoading(false));
  }, [yestimateId, navigate]);

  if (loading) return <div className="p-8 text-center">Loading preview...</div>;
  if (!preview) return <div className="p-8 text-center">Preview not found</div>;

  const jobGroups = preview.job_groups || [];

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b px-6 py-4">
        <div className="max-w-3xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/app/foh/yestimate/${yestimateId}/pricing`)} className="mb-2">
            ← Back to Pricing
          </Button>
          <div className="flex justify-between items-center">
            <h1 className="font-bold text-xl text-slate-800">Customer Preview</h1>
            <Badge className="bg-amber-100 text-amber-700">
              <Eye className="w-3 h-3 mr-1" /> Preview Mode
            </Badge>
          </div>
          <p className="text-sm text-slate-500 mt-1">Final review before sending to customer — verify all pricing and details are correct</p>
        </div>
      </header>

      {/* iter199ad — Portal vs Pricing parity banner. Fires at ANY
          non-zero delta (per user spec) so even a penny VAT-rounding
          mismatch is surfaced for FOH to investigate BEFORE the
          customer sees the link. The backend's compute_portal_parity
          helper mirrors the portal renderer's dedup contract, so any
          item that would be hidden from the portal but is on the
          pricing screen lands in `missing_from_portal` with the £
          value and the reason. */}
      {preview.portal_parity && !preview.portal_parity.matches && (
        <div className="max-w-3xl mx-auto px-6 mt-4" data-testid="portal-parity-banner">
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="text-xl leading-none">⚠️</span>
              <div className="flex-1">
                <div className="font-semibold text-amber-900">
                  Portal total doesn&rsquo;t match the pricing screen
                </div>
                <div className="text-sm text-amber-800 mt-1">
                  Pricing total:{" "}
                  <span className="font-mono font-semibold" data-testid="parity-pricing-total">
                    £{(preview.portal_parity.pricing_total || 0).toFixed(2)}
                  </span>
                  {" · "}Portal total:{" "}
                  <span className="font-mono font-semibold" data-testid="parity-portal-total">
                    £{(preview.portal_parity.portal_total || 0).toFixed(2)}
                  </span>
                  {" · "}Delta:{" "}
                  <span className="font-mono font-bold text-red-700" data-testid="parity-delta">
                    £{Math.abs(preview.portal_parity.delta || 0).toFixed(2)}
                  </span>
                </div>
                {Array.isArray(preview.portal_parity.missing_from_portal) &&
                  preview.portal_parity.missing_from_portal.length > 0 && (
                  <div className="mt-3 text-sm text-amber-900">
                    <div className="font-medium mb-1">
                      On pricing but hidden from portal:
                    </div>
                    <ul className="list-disc list-inside space-y-0.5" data-testid="parity-missing-list">
                      {preview.portal_parity.missing_from_portal.map((m, i) => (
                        <li key={m.id || i}>
                          <span className="font-medium">{m.title}</span>
                          {" "}— £{Number(m.price || 0).toFixed(2)}
                          {m.reason && (
                            <span className="text-amber-700 italic"> ({m.reason})</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-xs text-amber-700 mt-2">
                  Tip: open the pricing screen and check each item is set to show on portal,
                  or re-review the canonical dedup if two similar items are merging.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer View Simulation */}
      <div className="max-w-3xl mx-auto p-6">
        <Card className="shadow-lg">
          <CardContent className="p-8">
            {/* Garage Logo */}
            {garageLogo && (
              <div className="flex justify-center mb-6">
                <img 
                  src={garageLogo} 
                  alt={garageName || "Garage Logo"} 
                  className="max-h-16 object-contain"
                />
              </div>
            )}
            
            {/* Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Vehicle Inspection Report</h2>
              <p className="text-slate-600">{preview.vehicle}</p>
              <p className="text-sm text-slate-500">Mileage: {preview.mileage} miles</p>
            </div>

            {/* Greeting */}
            <p className="text-slate-700 mb-8">{preview.greeting}</p>

            {/* Health Indicators Panel */}
            {preview.health_indicators && preview.health_indicators.length > 0 && (
              <div className="mb-8 bg-slate-50 rounded-xl p-6 border border-slate-200">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <span className="text-blue-600">⚡</span> Vehicle Health Check
                </h3>
                <p className="text-sm text-slate-500 mb-4">Key safety and wear components</p>
                
                {/* Fluids & Battery - Circular indicators */}
                {preview.health_indicators.filter(i => !i.indicator_type).length > 0 && (
                  <div className="mb-6">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Fluids & Battery</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {preview.health_indicators.filter(i => !i.indicator_type).map((indicator, idx) => {
                        // Determine the proper label based on type
                        const displayLabel = indicator.is_brake_fluid ? 'Brake Fluid Health' : 
                                            indicator.is_battery ? 'Battery Health' :
                                            indicator.is_coolant ? 'Coolant Health' :
                                            indicator.label;
                        const showDegradation = indicator.is_brake_fluid || indicator.is_battery || indicator.is_coolant;
                        const degradationPct = indicator.degradation_percentage || (100 - indicator.health_percentage);
                        
                        return (
                          <div 
                            key={idx}
                            className={`rounded-lg p-3 border ${
                              indicator.health_color === 'green' ? 'bg-emerald-50 border-emerald-200' :
                              indicator.health_color === 'amber' ? 'bg-amber-50 border-amber-200' :
                              indicator.health_color === 'red' ? 'bg-red-50 border-red-200' :
                              'bg-white border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="relative w-12 h-12 flex-shrink-0" title={`Health: ${indicator.health_percentage}% | Degradation: ${degradationPct}%`}>
                                <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
                                  <circle className="text-slate-200" strokeWidth="3" stroke="currentColor" fill="transparent" r="14" cx="18" cy="18" />
                                  <circle
                                    className={indicator.health_color === 'green' ? 'text-emerald-500' : indicator.health_color === 'amber' ? 'text-amber-500' : indicator.health_color === 'red' ? 'text-red-500' : 'text-slate-400'}
                                    strokeWidth="3" strokeLinecap="round" stroke="currentColor" fill="transparent" r="14" cx="18" cy="18"
                                    strokeDasharray={`${(indicator.health_percentage || 0) * 0.88} 100`}
                                  />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className={`text-xs font-bold ${indicator.health_color === 'green' ? 'text-emerald-700' : indicator.health_color === 'amber' ? 'text-amber-700' : indicator.health_color === 'red' ? 'text-red-700' : 'text-slate-600'}`}>
                                    {indicator.health_percentage}%
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-slate-700">
                                  {displayLabel}
                                </div>
                                <div className={`text-xs font-semibold ${indicator.health_color === 'green' ? 'text-emerald-600' : indicator.health_color === 'amber' ? 'text-amber-600' : indicator.health_color === 'red' ? 'text-red-600' : 'text-slate-500'}`}>
                                  {indicator.health_label}
                                </div>
                                <div className="text-xs text-slate-400">
                                  {indicator.measurement_value}{indicator.measurement_unit}
                                  {showDegradation && (
                                    <span className="ml-1 text-slate-500">
                                      ({degradationPct}% degraded)
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {showDegradation && (
                              <p className="text-xs text-slate-400 italic mt-2 pt-2 border-t border-slate-200/50">
                                Health % = serviceability. Degradation % = 100 − Health %.
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Tyre Indicators - Horizontal Bars */}
                {preview.health_indicators.filter(i => i.indicator_type === 'tyre').length > 0 && (
                  <div className="mb-6">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Tyre Tread Depth</p>
                    <div className="grid grid-cols-2 gap-3">
                      {preview.health_indicators.filter(i => i.indicator_type === 'tyre').map((indicator, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3 border border-slate-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-700">{indicator.label}</span>
                            <span className={`text-sm font-bold ${indicator.health_color === 'green' ? 'text-emerald-600' : indicator.health_color === 'amber' ? 'text-amber-600' : 'text-red-600'}`}>
                              {indicator.measurement_value}mm
                            </span>
                          </div>
                          <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${indicator.health_color === 'green' ? 'bg-emerald-500' : indicator.health_color === 'amber' ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${indicator.health_percentage}%` }}
                            />
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <span className={`text-xs ${indicator.health_color === 'green' ? 'text-emerald-600' : indicator.health_color === 'amber' ? 'text-amber-600' : 'text-red-600'}`}>
                              {indicator.health_label}
                            </span>
                            <span className="text-xs text-slate-400">{indicator.health_percentage}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Brake Pad Indicators - Horizontal Bars */}
                {preview.health_indicators.filter(i => i.indicator_type === 'brake_pads').length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Brake Pad Thickness</p>
                    <div className="grid grid-cols-2 gap-3">
                      {preview.health_indicators.filter(i => i.indicator_type === 'brake_pads').map((indicator, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3 border border-slate-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-700">{indicator.label}</span>
                            <span className={`text-sm font-bold ${indicator.health_color === 'green' ? 'text-emerald-600' : indicator.health_color === 'amber' ? 'text-amber-600' : 'text-red-600'}`}>
                              {indicator.measurement_value}mm
                            </span>
                          </div>
                          <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${indicator.health_color === 'green' ? 'bg-emerald-500' : indicator.health_color === 'amber' ? 'bg-amber-500' : 'bg-red-500'}`}
                              style={{ width: `${indicator.health_percentage}%` }}
                            />
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <span className={`text-xs ${indicator.health_color === 'green' ? 'text-emerald-600' : indicator.health_color === 'amber' ? 'text-amber-600' : 'text-red-600'}`}>
                              {indicator.health_label}
                            </span>
                            <span className="text-xs text-slate-400">{indicator.health_percentage}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Sections - Findings */}
            {preview.sections.map((section, idx) => (
              <div key={idx} className={`mb-6 ${section.type === 'dangerous' ? 'bg-black text-white rounded-xl p-4' : ''}`}>
                <div className={`font-semibold mb-2 ${
                  section.type === "dangerous" ? "text-white text-lg" :
                  section.type === "red" ? "text-red-700" : 
                  section.type === "amber" ? "text-amber-700" : "text-green-700"
                }`}>
                  {section.title}
                </div>
                <p className={`text-sm mb-3 ${section.type === 'dangerous' ? 'text-gray-300' : 'text-slate-600'}`}>{section.intro}</p>
                <ul className="space-y-2">
                  {section.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex items-start gap-2">
                      <div className={`w-2 h-2 rounded-full mt-2 ${
                        section.type === "dangerous" ? "bg-white" :
                        section.type === "red" ? "bg-red-500" : 
                        section.type === "amber" ? "bg-amber-500" : "bg-green-500"
                      }`} />
                      <div>
                        <p className={section.type === 'dangerous' ? 'text-white font-semibold' : 'text-slate-800'}>{item.label}</p>
                        {item.notes && <p className={`text-sm ${section.type === 'dangerous' ? 'text-gray-300' : 'text-slate-500'}`}>{item.notes}</p>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Job-Based Pricing */}
            {jobGroups.length > 0 && (
              <>
                <Separator className="my-6" />
                <div className="mb-6">
                  <h3 className="font-semibold text-slate-800 mb-4">Recommended Work</h3>
                  <div className="space-y-4">
                    {/* Sort job groups by priority: DIAGNOSTIC > DANGEROUS > RED > AMBER > other */}
                    {[...jobGroups].sort((a, b) => {
                      const getPriority = (jg) => {
                        // Diagnostic jobs first
                        if (jg.source === 'diagnostic' || jg.vi_item_key?.includes('warning') || jg.vi_item_key?.includes('dash_warning')) return 0;
                        // Dangerous items second
                        if (jg.vi_status === 'dangerous') return 1;
                        // Red items third
                        if (jg.vi_status === 'red' || jg.is_mandatory) return 2;
                        // Amber items fourth
                        if (jg.vi_status === 'amber') return 3;
                        // Everything else
                        return 4;
                      };
                      return getPriority(a) - getPriority(b);
                    }).map((job, jobIdx) => {
                      const isDiagnosticJob = job.source === 'diagnostic' || job.vi_item_key?.includes('warning') || job.vi_item_key?.includes('dash_warning');
                      return (
                      <div key={jobIdx} className={`border rounded-lg p-4 ${
                        job.vi_status === 'dangerous' ? 'bg-black text-white border-black' :
                        isDiagnosticJob ? 'bg-purple-50 border-purple-200' :
                        job.vi_status === 'red' || job.is_mandatory ? 'bg-red-50 border-red-200' :
                        job.vi_status === 'amber' ? 'bg-amber-50 border-amber-200' :
                        'bg-slate-50'
                      }`}>
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-center gap-2">
                            <h4 className={`font-semibold ${job.vi_status === 'dangerous' ? 'text-white' : 'text-slate-800'}`}>{job.title}</h4>
                            {isDiagnosticJob && !job.vi_status?.includes('dangerous') && (
                              <span className="bg-purple-600 text-white text-xs px-2 py-0.5 rounded">DIAGNOSTIC</span>
                            )}
                            {job.vi_status === 'dangerous' && (
                              <span className="bg-white text-black text-xs px-2 py-0.5 rounded font-bold">⚠️ DANGEROUS</span>
                            )}
                            {job.vi_status === 'red' && !job.vi_status === 'dangerous' && (
                              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded">RED</span>
                            )}
                            {job.vi_status === 'amber' && (
                              <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded">ADVISORY</span>
                            )}
                          </div>
                          <span className={`font-bold ${job.vi_status === 'dangerous' ? 'text-white' : 'text-slate-800'}`}>£{job.total?.toFixed(2)} inc VAT</span>
                        </div>
                        
                        {/* Labour */}
                        {job.labour_lines?.length > 0 && (
                          <div className="space-y-1 text-sm">
                            {job.labour_lines.map((labour, lIdx) => (
                              <div key={lIdx} className={`flex justify-between ${job.vi_status === 'dangerous' ? 'text-gray-300' : 'text-slate-600'}`}>
                                <span>Labour: {labour.category_label} – {labour.hours} hr{labour.hours !== 1 ? "s" : ""}</span>
                                <span>£{labour.total?.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Parts */}
                        {job.parts_lines?.length > 0 && (
                          <div className="space-y-1 text-sm mt-1">
                            {job.parts_lines.map((part, pIdx) => (
                              <div key={pIdx} className={`flex justify-between ${job.vi_status === 'dangerous' ? 'text-gray-300' : 'text-slate-600'}`}>
                                <span>Parts: {part.description}{part.quantity > 1 ? ` x${part.quantity}` : ""}</span>
                                <span>£{part.total?.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Charges */}
                        {job.charges?.length > 0 && (
                          <div className="space-y-1 text-sm mt-1">
                            {job.charges.map((charge, cIdx) => (
                              <div key={cIdx} className={`flex justify-between ${job.vi_status === 'dangerous' ? 'text-gray-400' : 'text-slate-500'}`}>
                                <span>{charge.description}</span>
                                <span>£{charge.total?.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )})}
                  </div>
                  
                  <Separator className="my-4" />
                  <div className="space-y-1" data-testid="foh-customer-preview-breakdown">
                    {/* iter197i — Itemised breakdown so FOH can verify
                        every component of the customer total before
                        sending. Backend `/preview` returns the
                        breakdown so the FE doesn't have to know
                        workshop magic numbers. */}
                    {preview.totals?.breakdown ? (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Jobs ({jobGroups.length}) (ex VAT)</span>
                          <span data-testid="preview-jobs-subtotal">£{(preview.totals.breakdown.jobs_subtotal ?? 0).toFixed(2)}</span>
                        </div>
                        {preview.totals.breakdown.consumables_applied && (
                          <div className="flex justify-between text-sm" data-testid="preview-consumables-line">
                            <span className="text-slate-500">Workshop Consumables (ex VAT)</span>
                            <span>£{(preview.totals.breakdown.consumables_amt ?? 0).toFixed(2)}</span>
                          </div>
                        )}
                        {preview.totals.breakdown.environmental_applied && (
                          <div className="flex justify-between text-sm" data-testid="preview-environmental-line">
                            <span className="text-slate-500">Environmental &amp; Disposal (ex VAT)</span>
                            <span>£{(preview.totals.breakdown.environmental_amt ?? 0).toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm pt-1.5 mt-1.5 border-t border-slate-200">
                          <span className="text-slate-600 font-medium">Subtotal (ex VAT)</span>
                          <span className="font-medium" data-testid="preview-subtotal">£{(preview.totals?.subtotal ?? 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">VAT (20%)</span>
                          <span data-testid="preview-vat">£{(preview.totals?.vat ?? 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm pt-1.5 mt-1.5 border-t border-slate-200">
                          <span className="text-slate-600 font-medium">New Work Total (inc VAT)</span>
                          <span className="font-medium" data-testid="preview-new-work-total">£{(preview.totals.breakdown.new_work_total ?? 0).toFixed(2)}</span>
                        </div>
                        {(preview.totals.breakdown.already_authorised || 0) > 0 && (
                          <div className="flex justify-between text-sm text-emerald-700" data-testid="preview-already-authorised-line">
                            <span className="flex items-center gap-1.5">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              {preview.totals.breakdown.already_authorised_label
                                ? `${preview.totals.breakdown.already_authorised_label} (already booked)`
                                : "Previously Authorised"}
                            </span>
                            <span>+£{(preview.totals.breakdown.already_authorised || 0).toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-bold text-lg pt-2 mt-2 border-t border-slate-300">
                          <span>Total</span>
                          <span data-testid="preview-customer-total">£{(preview.totals?.total ?? 0).toFixed(2)} inc VAT</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Subtotal (ex VAT)</span>
                          <span>£{preview.totals?.subtotal?.toFixed(2) || "0.00"}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">VAT</span>
                          <span>£{preview.totals?.vat?.toFixed(2) || "0.00"}</span>
                        </div>
                        <div className="flex justify-between font-bold text-lg">
                          <span>Total</span>
                          <span>£{preview.totals?.total?.toFixed(2) || "0.00"} inc VAT</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Closing */}
            <p className="text-slate-700 mt-6">{preview.closing}</p>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="mt-6 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={() => navigate(`/app/foh/yestimate/${yestimateId}/pricing`)}>
            Edit Pricing
          </Button>
          <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => navigate(`/app/foh/yestimate/${yestimateId}/portal-preview`)} data-testid="send-to-customer-btn">
            <Send className="w-4 h-4 mr-2" /> Send to Customer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FOHCustomerPreview;
